import type { MouseEvent } from 'react'
import { useState } from 'react'
import { useIntl } from 'react-intl'

import type {
  DataTableProps as DsDataTableProps,
  OnChangeFn,
  PaginationState,
  Row,
  SortingState,
} from '@gto/ui/data-table'
import { DataTable as DsDataTable, readColumnMeta, TablePager } from '@gto/ui/data-table'
import { Paper } from '@gto/ui/paper'

/**
 * The portal's one table. Wraps the DS `DataTable` (TanStack) with the house card and pager so
 * every page renders the same table instead of 19 slightly different ones.
 *
 * DENSITY is DERIVED, not defaulted and not per call site: the old grid ran 44px rows, which the
 * DS density axis has no value for (`standard` 40px, `comfortable` 50px), so picking one for all
 * 19 tables would misstate it — but leaving it to each page meant a table that grew a two-line
 * cell clipped it silently, which happened three times. A column marks itself `stacked`
 * (`fluidColumn({ stacked: true })`) and this wrapper raises the row height once. An explicit
 * `density` still wins.
 * Columns are TanStack `ColumnDef`s built with `createColumns<Row>()` — see
 * `useCommissionColumns` for the reference shape.
 *
 * Everything the DS preset accepts passes straight through. This adds what the call sites
 * all wanted from the old DataGridPro wrapper — a `loading` boolean, a row-click handler,
 * a row class resolver, pagination ON by default at 10 rows, and a pager that appears exactly
 * when the rows overflow one page — plus one correctness seam:
 *
 * TanStack's `onXChange` handlers receive an UPDATER FUNCTION, not a value, so a caller
 * writing `onPaginationChange={(next) => next.pageIndex}` reads a function and silently
 * breaks. The pagination and sorting callbacks are re-typed to hand over a plain VALUE,
 * with the updater resolved here once rather than at every call site.
 *
 * Empty-state copy is deliberately NOT defaulted — each page owns its `<feature>.empty`
 * message and passes it through `stateCopy`.
 */

const DEFAULT_PAGE_SIZE = 10
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50]

/**
 * The starting page size for an uncontrolled table, held to a size the pager actually
 * offers: its select renders no option for a value it was not given, so an unlisted one
 * would strand the control on a blank — the very bug `defaultPageSize` exists to prevent.
 */
const initialPageSize = (preferred: number, options: number[]): number =>
  options.includes(preferred) ? preferred : (options.at(0) ?? DEFAULT_PAGE_SIZE)

/**
 * Controls whose click is THEIRS, not the row's — the row-actions menu trigger, an inline
 * navigate button, a copy button, a checkbox.
 *
 * DataGridPro delegated row clicks through its own event system and action cells escaped
 * it; here `onClick` sits on the row div, so a click on a nested control bubbles straight
 * into the row handler. Without this guard, opening a row's actions menu ALSO fires the
 * row click — on the tiers page that opened the edit dialog behind the delete
 * confirmation, leaving two dialogs stacked. Per-control `stopPropagation` is not
 * reliable enough: Base UI's `render`-prop trigger merges its own handlers over the
 * ones passed to the rendered element.
 */
const isInteractiveTarget = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  target.closest('button, a, input, select, textarea, [role="menuitem"], [role="checkbox"]') !==
    null

/**
 * The prop pairings this wrapper cannot express in the type system, reported in DEV.
 *
 * Every one is a caller mistake that otherwise fails SILENTLY — the table keeps rendering while
 * doing something other than what the call site reads as. Grouped here so the contract is one
 * list instead of conditionals interleaved with the render.
 */
const warnOnContractMistakes = ({
  defaultPageSize,
  manualPagination,
  onPaginationChange,
  onSortingChange,
  pageSizeOptions,
  pagination,
  rowCount,
  sorting,
}: {
  defaultPageSize: number
  manualPagination?: boolean
  onPaginationChange?: (next: PaginationState) => void
  onSortingChange?: (next: SortingState) => void
  pageSizeOptions: number[]
  pagination?: PaginationState
  rowCount?: number
  sorting?: SortingState
}) => {
  if (!import.meta.env.DEV) {
    return
  }

  if (!pageSizeOptions.includes(defaultPageSize)) {
    console.warn(
      `DataTable: defaultPageSize ${defaultPageSize} is not one of pageSizeOptions [${pageSizeOptions.join(', ')}] — the pager renders no option for a size it was not offered, so it would show a blank value. Falling back to ${initialPageSize(defaultPageSize, pageSizeOptions)}.`,
    )
  }
  if (pagination !== undefined && onPaginationChange === undefined) {
    console.warn(
      'DataTable: `pagination` without `onPaginationChange` is ignored — the table rides its own state. Pass both, or neither.',
    )
  }
  if (sorting !== undefined && onSortingChange === undefined) {
    console.warn(
      'DataTable: `sorting` without `onSortingChange` cannot be updated — every updater resolves against the value you passed. Pass both, or neither.',
    )
  }
  if (manualPagination === true && rowCount === undefined) {
    console.warn(
      'DataTable: `manualPagination` without `rowCount` — the engine only holds the page it was handed, so the pager cannot size itself and pages beyond the first become unreachable. Pass the server total.',
    )
  }
}

export type DataTableProps<R extends object> = Omit<
  DsDataTableProps<R>,
  // `children` mounts the pager; `query` is derived from `loading` + `refetching`; the two
  // change handlers are re-typed to value-based below. Checkbox selection stays DS-native —
  // pass the `selection` prop, which is what renders the selection column.
  | 'children'
  | 'query'
  | 'onPaginationChange'
  | 'onSortingChange'
  // Pinned off for every table in the portal — see the note at the render site.
  | 'showColumnMenu'
  | 'showColumnVisibilityMenu'
> & {
  /** FIRST load — no rows yet. Drives the DS skeleton (`isPending`). */
  loading?: boolean
  /**
   * A refetch is serving the PREVIOUS rows (`keepPreviousData` / `isPlaceholderData`).
   * Kept apart from `loading` because it means something different to the DS: stale rows
   * stay on screen under a refetch overlay, and the preset gates selection and focus on
   * it, so a select-all during the window can't bind to rows about to be replaced.
   */
  refetching?: boolean
  /**
   * Total matching rows. REQUIRED alongside `manualPagination`: the engine only holds
   * the page it was handed, so nothing else can size the pager.
   */
  rowCount?: number
  /** Rows-per-page choices. Defaults to 10 / 25 / 50. */
  pageSizeOptions?: number[]
  /**
   * Starting rows-per-page for a table that does NOT control `pagination`. Defaults to 10
   * (DataGridPro wrapper parity). It must be one of `pageSizeOptions` — the pager's select
   * renders no option for a size it was not offered, so a mismatch strands it on a blank
   * value, which is why this exists rather than callers reaching for `initialState`.
   */
  defaultPageSize?: number
  /**
   * FORCE the pager off. Normally it is derived — it shows exactly when the rows do not fit
   * one page (`total > pageSize`), so no caller has to keep a row-count threshold in sync
   * with a page size. That hand-sync is what stranded rows 11-50 on the report tables:
   * `hidePager={rows.length <= 50}` against a 10-row page meant no pager AND no way to
   * reach page 2. Pass this only for a list that must never page regardless.
   */
  hidePager?: boolean
  onRowClick?: (row: R) => void
  /** Extra classes per row, for status-driven dimming (reverted, locked). */
  getRowClassName?: (row: R) => string | undefined
  onPaginationChange?: (next: PaginationState) => void
  onSortingChange?: (next: SortingState) => void
}

export const DataTable = <R extends object>({
  density,
  loading,
  refetching,
  rowCount,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  hidePager = false,
  onRowClick,
  getRowClassName,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  getRowProps,
  ...props
}: DataTableProps<R>) => {
  const { formatMessage } = useIntl()

  // Pagination is on by default (DataGridPro wrapper parity). A caller that controls it
  // passes `pagination` + `onPaginationChange`; everyone else rides this internal state,
  // so a plain `<DataTable columns data />` still pages at 10 — or at `defaultPageSize`,
  // this tier's replacement for DataGridPro's `initialState.pagination`.
  warnOnContractMistakes({
    defaultPageSize,
    manualPagination: props.manualPagination,
    onPaginationChange,
    onSortingChange,
    pageSizeOptions,
    pagination,
    rowCount,
    sorting,
  })

  const [ownPagination, setOwnPagination] = useState<PaginationState>(() => ({
    pageIndex: 0,
    pageSize: initialPageSize(defaultPageSize, pageSizeOptions),
  }))
  const isPaginationControlled = pagination !== undefined && onPaginationChange !== undefined
  // Half-controlled is a caller mistake, not a mode: with only one of the pair the table
  // would quietly ride internal state (or resolve every sort updater against `[]`) and the
  // caller's own state would drift out of view. Dev-only, like the DS's own contract warns.
  const activePagination = isPaginationControlled ? pagination : ownPagination

  const emitPagination = (next: PaginationState) => {
    if (isPaginationControlled) {
      onPaginationChange(next)
    } else {
      setOwnPagination(next)
    }
  }

  const handlePaginationChange: OnChangeFn<PaginationState> = (updaterOrValue) => {
    emitPagination(
      typeof updaterOrValue === 'function' ? updaterOrValue(activePagination) : updaterOrValue,
    )
  }

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    onSortingChange?.(
      typeof updaterOrValue === 'function' ? updaterOrValue(sorting ?? []) : updaterOrValue,
    )
  }

  const resolveRowProps = (row: Row<R>) => {
    const fromCaller = getRowProps?.(row) ?? {}
    const rowClassName = getRowClassName?.(row.original)

    return {
      ...fromCaller,
      // DataGridPro stamped every row with `data-id`; keep an equivalent so a test or
      // e2e spec can address one row by its identity rather than its position.
      'data-row-id': row.id,
      className: [rowClassName, fromCaller.className].filter(Boolean).join(' ') || undefined,
      interactive: onRowClick !== undefined || fromCaller.interactive === true,
      onClick:
        onRowClick === undefined
          ? fromCaller.onClick
          : (event: MouseEvent<HTMLDivElement>) => {
              fromCaller.onClick?.(event)
              if (isInteractiveTarget(event.target)) {
                return
              }
              onRowClick(row.original)
            },
    }
  }

  // Server pagination knows its total only from `rowCount`; the client tier counts the
  // rows it was handed.
  const total = rowCount ?? props.data?.length ?? 0
  // Derived, never hand-synced: the pager exists when the rows could overflow a page at ALL.
  //
  // Measured against the SMALLEST offered size, not the active one — the active size is set BY
  // this control, so `total > activePagination.pageSize` let it delete itself: 30 rows, pick 50,
  // and the select that just changed the size unmounts with no way back. Worse on the server
  // tier, where the choice rides the URL (`?size=`) and so survives a reload.
  //
  // `hidePager` remains a deliberate override.
  // Density follows the CELL, not the call site: a column whose cell stacks two lines does not
  // fit the `standard` 40px row, and the second line clips with nothing to catch it. Reading the
  // requirement off the columns means a table cannot forget it, and a conditionally-included
  // stacked column (payouts' optional partner column) needs no special case. An explicit
  // `density` still wins.
  const hasStackedColumn = props.columns.some(
    (column) => readColumnMeta({ columnDef: column })?.stacked === true,
  )
  const resolvedDensity = density ?? (hasStackedColumn ? 'comfortable' : undefined)

  const smallestPageSize = Math.min(...pageSizeOptions, activePagination.pageSize)
  const showPager = !hidePager && total > smallestPageSize

  return (
    // ONE card holding the grid AND the pager, which is what the DataGridPro wrapper did
    // (`<Paper elevation="2" rounded-md>` around the grid and its footer): the pager sits
    // INSIDE the card on the footer gradient rather than floating underneath it as a
    // detached strip of controls.
    //
    // The table below is `embedded` BECAUSE of this Paper: two rounded clips at different radii
    // carve a visible notch out of the inner one's corners. `embedded` hands the silhouette to
    // the Paper, which is how the old wrapper worked too — the grid set no radius of its own.
    <Paper
      className="flex flex-col overflow-hidden rounded-md"
      // The pager belonging INSIDE this card is a structural contract, not a style — named
      // so a test can assert it rather than inferring it from a class.
      data-tst="data-table-card"
      elevation="2"
    >
      <DsDataTable<R>
        embedded
        density={resolvedDensity}
        // Painted at the CARD's elevation, not the DS's default `elevation3`: that one step up
        // reads as a grey band. With `rowDivider` also dropped, these tables ride the DS's own
        // chrome instead of DataGridPro's. The header still reads — `text-table-header` grey
        // against primary text, and sticky.
        headerSurface="elevation2"
        pagination={activePagination}
        // `isFetching` is deliberately NOT aliased onto `refetching`: the DS already ORs the
        // two for its overlay, and claiming it here would mean a background refetch (an
        // invalidation after a mutation) could not be signalled apart from a stale-rows one.
        // Both fields stay optional, and the DS reads each as `?? false`, so a table that
        // passes neither still collapses to the client tier's Populated ⇄ Empty.
        query={{ isPending: loading, placeholderData: refetching }}
        sorting={sorting}
        onPaginationChange={handlePaginationChange}
        onSortingChange={onSortingChange === undefined ? undefined : handleSortingChange}
        {...props}
        // AFTER the spread, and omitted from this wrapper's props type: both of DataGridPro's
        // menus stay off for every table in the portal, so a caller cannot re-enable them by
        // accident. `showColumnVisibilityMenu` is `disableColumnSelector` (the DS defaults that
        // "Columns" button ON for any column that can hide — every one, since nothing sets
        // `enableHiding` — and it lists raw column ids in untranslated English);
        // `showColumnMenu` is `disableColumnMenu` (the per-column three-dot menu, which the DS
        // auto-enables the moment a table engages pinning or reordering).
        getRowProps={resolveRowProps}
        showColumnMenu={false}
        showColumnVisibilityMenu={false}
      />

      {showPager && (
        <TablePager
          // The footer band the old grid painted with `theme.gradients.paper1`.
          className="bg-gradient-paper1"
          pageIndex={activePagination.pageIndex}
          pageSize={activePagination.pageSize}
          pageSizeOptions={pageSizeOptions}
          total={total}
          labels={{
            nextPageAriaLabel: formatMessage({ id: 'common.table.nextPage' }),
            previousPageAriaLabel: formatMessage({ id: 'common.table.previousPage' }),
            renderRange: ({ from, to, total: rangeTotal }) =>
              formatMessage({ id: 'common.table.pageRange' }, { from, to, total: rangeTotal }),
            rowsPerPage: formatMessage({ id: 'common.table.rowsPerPage' }),
            rowsPerPageAriaLabel: formatMessage({ id: 'common.table.rowsPerPage' }),
          }}
          onPageChange={(pageIndex) => {
            emitPagination({ ...activePagination, pageIndex })
          }}
          onPageSizeChange={(pageSize) => {
            // Reset to the first page: a stale index can land past the new last page and
            // render an empty table (DataGridPro's pager reset too).
            emitPagination({ pageIndex: 0, pageSize })
          }}
        />
      )}
    </Paper>
  )
}
